// Service location utility functions
import { 
  DEFAULT_SERVICE_TYPES,
  type ServiceLocationData,
  type ServiceLocationConfig,
  type ServiceTypeInfo,
  type ServiceTypeCode
} from './serviceLocationTypes';
import { debugLog } from '../store-layer/debugUtils';

const SERVICE_PIN_LAYER_ID = 'service-pins-layer';
const SERVICE_LABEL_LAYER_ID = 'service-labels-layer';

// Google Material Symbols Outlined mapping for service types
// Remember to update the link in SVGMap.astro when adding/removing icons (alphabetically)
const GOOGLE_ICON_MAPPING: Record<string, string> = {
  parking: 'local_parking',
  toilet: 'wc',
  elevator: 'elevator',
  atm: 'local_atm',
  escalator: 'escalator',
  stairs: 'stairs_2',
  accessible_wc: 'accessible',
  bottle_recycling: 'recycling',
  playground: 'playground_2',
  nursery_room: 'baby_changing_station',
  service_desk: 'info',
  directory: 'distance',
  taxi: 'local_taxi',
  train: 'train',
  metro: 'metro',
  tram: 'tram',
  bus: 'directions_bus',
  stop: 'directions_bus',
  recycling: 'recycling'
};

/**
 * Get service type information by ID
 */
export function getServiceTypeById(serviceTypeId: number): ServiceTypeInfo | null {
  return DEFAULT_SERVICE_TYPES.find(type => type.id === serviceTypeId) || null;
}

/**
 * Get service type information by code
 */
export function getServiceTypeByCode(code: ServiceTypeCode): ServiceTypeInfo | null {
  return DEFAULT_SERVICE_TYPES.find(type => type.code === code) || null;
}

/**
 * Create a Google Material Icon pin
 */
function createGoogleIconPin(
  service: ServiceLocationData,
  serviceType: ServiceTypeInfo,
  config: ServiceLocationConfig,
  adjustedX: number,
  adjustedY: number,
  iconName: string
): SVGElement | null {
  // Get the Material Symbol name from the mapping
  const materialSymbolName = GOOGLE_ICON_MAPPING[iconName];
  if (!materialSymbolName) {
    debugLog(`No Material Symbol mapping found for "${iconName}", falling back to default pin for service ${service.id}`, 'serviceLocations');
    return null;
  }

  const pinContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  pinContainer.setAttribute('data-service-id', service.id.toString());
  pinContainer.setAttribute('data-service-type', serviceType.code);
  pinContainer.classList.add('service-pin-container');

  const scale = config.pinStyling.scale;
  const yOffset = config.pinStyling.yOffset || 0;
  const iconSize = config.pinStyling.iconSize * scale;

  // Add optional background circle for better visibility
  if (config.pinStyling.showIconBackground) {
    const backgroundCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    backgroundCircle.setAttribute('cx', String(adjustedX));
    backgroundCircle.setAttribute('cy', String(adjustedY + yOffset));
    backgroundCircle.setAttribute('r', String(config.iconBackgroundStyling.radius * scale));
    backgroundCircle.setAttribute('fill', config.iconBackgroundStyling.fillColor.inactive);
    backgroundCircle.setAttribute('stroke', config.iconBackgroundStyling.strokeColor.inactive);
    backgroundCircle.setAttribute('stroke-width', config.iconBackgroundStyling.strokeWidth.inactive);
    backgroundCircle.setAttribute('data-service-id', service.id.toString());
    backgroundCircle.classList.add('service-pin', 'service-pin-background');
    
    pinContainer.appendChild(backgroundCircle);
  }

  // Create a foreign object to contain the Material Icon
  const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
  foreignObject.setAttribute('x', String(adjustedX - iconSize / 2));
  foreignObject.setAttribute('y', String(adjustedY - iconSize / 2 + yOffset));
  foreignObject.setAttribute('width', String(iconSize));
  foreignObject.setAttribute('height', String(iconSize));
  foreignObject.setAttribute('data-service-id', service.id.toString());
  foreignObject.classList.add('service-pin', 'service-pin-google-icon');

  // Create the Material Symbol span with the mapped icon name
  const iconSpan = document.createElement('span');
  iconSpan.className = 'material-symbols-outlined';
  iconSpan.textContent = materialSymbolName;
  iconSpan.style.fontSize = `${iconSize}px`;
  iconSpan.style.color = config.googleIconStyling.color.inactive;
  iconSpan.style.display = 'flex';
  iconSpan.style.alignItems = 'center';
  iconSpan.style.justifyContent = 'center';
  iconSpan.style.width = '100%';
  iconSpan.style.height = '100%';
  iconSpan.style.lineHeight = '1';

  // Debug: Check icon creation
  debugLog(`Creating Material Symbol span for "${materialSymbolName}" with fontSize ${iconSize}px, color ${config.googleIconStyling.color.inactive}`, 'serviceLocations');

  foreignObject.appendChild(iconSpan);
  pinContainer.appendChild(foreignObject);

  debugLog(`Created Google Material Symbol pin for service ${service.id} with icon: ${materialSymbolName}`, 'serviceLocations');

  return pinContainer;
}

/**
 * Create a default circle pin with dot inside
 */
function createDefaultServicePin(
  service: ServiceLocationData,
  serviceType: ServiceTypeInfo,
  config: ServiceLocationConfig,
  adjustedX: number,
  adjustedY: number
): SVGElement {
  const pinContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  pinContainer.setAttribute('data-service-id', service.id.toString());
  pinContainer.setAttribute('data-service-type', serviceType.code);
  pinContainer.classList.add('service-pin-container');

  const scale = config.pinStyling.scale;
  const yOffset = config.pinStyling.yOffset || 0;
  
  // Create outer circle
  const outerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  outerCircle.setAttribute('cx', String(adjustedX));
  outerCircle.setAttribute('cy', String(adjustedY + yOffset));
  outerCircle.setAttribute('r', String(8 * scale));
  outerCircle.setAttribute('fill', config.pinStyling.fillColor.inactive);
  outerCircle.setAttribute('stroke', config.pinStyling.strokeColor.inactive);
  outerCircle.setAttribute('stroke-width', config.pinStyling.strokeWidth.inactive);
  outerCircle.setAttribute('data-service-id', service.id.toString());
  outerCircle.classList.add('service-pin', 'service-pin-outer');

  // Create inner dot
  const innerDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  innerDot.setAttribute('cx', String(adjustedX));
  innerDot.setAttribute('cy', String(adjustedY + yOffset));
  innerDot.setAttribute('r', String(3 * scale));
  innerDot.setAttribute('fill', config.pinStyling.strokeColor.inactive);
  innerDot.setAttribute('data-service-id', service.id.toString());
  innerDot.classList.add('service-pin', 'service-pin-inner');

  pinContainer.appendChild(outerCircle);
  pinContainer.appendChild(innerDot);

  return pinContainer;
}

/**
 * Create a custom icon pin using fragment configuration
 */
async function createCustomIconPin(
  service: ServiceLocationData,
  serviceType: ServiceTypeInfo,
  config: ServiceLocationConfig,
  adjustedX: number,
  adjustedY: number,
  iconUrl: string
): Promise<SVGElement> {
  const pinContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  pinContainer.setAttribute('data-service-id', service.id.toString());
  pinContainer.setAttribute('data-service-type', serviceType.code);
  pinContainer.classList.add('service-pin-container');

  const scale = config.pinStyling.scale;
  const yOffset = config.pinStyling.yOffset || 0;
  const iconSize = config.pinStyling.iconSize * scale; // Use configurable icon size

  try {
    // Add optional background circle for better visibility
    if (config.pinStyling.showIconBackground) {
      const backgroundCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      backgroundCircle.setAttribute('cx', String(adjustedX));
      backgroundCircle.setAttribute('cy', String(adjustedY + yOffset));
      backgroundCircle.setAttribute('r', String(config.iconBackgroundStyling.radius * scale));
      backgroundCircle.setAttribute('fill', config.iconBackgroundStyling.fillColor.inactive);
      backgroundCircle.setAttribute('stroke', config.iconBackgroundStyling.strokeColor.inactive);
      backgroundCircle.setAttribute('stroke-width', config.iconBackgroundStyling.strokeWidth.inactive);
      backgroundCircle.setAttribute('data-service-id', service.id.toString());
      backgroundCircle.classList.add('service-pin', 'service-pin-background');
      
      pinContainer.appendChild(backgroundCircle);
    }

    // Create image element
    const imageElement = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    imageElement.setAttribute('href', iconUrl);
    imageElement.setAttribute('x', String(adjustedX - iconSize / 2));
    imageElement.setAttribute('y', String(adjustedY - iconSize / 2 + yOffset));
    imageElement.setAttribute('width', String(iconSize));
    imageElement.setAttribute('height', String(iconSize));
    imageElement.setAttribute('data-service-id', service.id.toString());
    imageElement.classList.add('service-pin', 'service-pin-icon');

    pinContainer.appendChild(imageElement);
    
    debugLog(`Created custom icon pin for service ${service.id} with icon: ${iconUrl}`, 'serviceLocations');
  } catch (error) {
    debugLog(`Failed to load custom icon for service ${service.id}, falling back to default pin`, 'serviceLocations');
    // Fall back to default pin if icon fails to load
    return createDefaultServicePin(service, serviceType, config, adjustedX, adjustedY);
  }

  return pinContainer;
}

/**
 * Create service location pins for a specific floor
 */
export async function createServicePinsForFloor(
  svgElement: SVGSVGElement,
  serviceLocations: ServiceLocationData[],
  config: ServiceLocationConfig
): Promise<void> {
  if (!svgElement || !config.enabled) return;
  
  debugLog(`Creating service pins for ${serviceLocations.length} service locations`, 'serviceLocations');

  if (serviceLocations.length === 0) {
    return;
  }

  // Clear existing pins
  clearServicePins(svgElement);

  // Create or get the service pin layer
  let pinLayer = svgElement.querySelector(`#${SERVICE_PIN_LAYER_ID}`);
  if (!pinLayer) {
    pinLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    pinLayer.setAttribute('id', SERVICE_PIN_LAYER_ID);
    svgElement.appendChild(pinLayer);
  }

  // Process each service location
  for (const service of serviceLocations) {
    const serviceType = getServiceTypeById(service.serviceType);
    if (!serviceType) continue;

    // Apply coordinate transformations for alignment with backend map editor
    let adjustedX = service.x;
    let adjustedY = service.y;
    
    // Apply scaling transformation if enabled
    if (config.coordinateScaling.enabled) {
      const scaleFactor = config.coordinateScaling.scaleFactor;
      const centerX = config.coordinateScaling.centerX;
      const centerY = config.coordinateScaling.centerY;
      
      // Scale the service location coordinates toward/away from the center
      adjustedX = centerX + (service.x - centerX) * scaleFactor;
      adjustedY = centerY + (service.y - centerY) * scaleFactor;
    }
    
    // Apply coordinate offset
    adjustedX += config.coordinateOffset.x;
    adjustedY += config.coordinateOffset.y;
    
    // Debug logging for coordinate positioning
    debugLog(`Service ${service.id} (${serviceType.name}): Backend coords (${service.x}, ${service.y}) → Adjusted coords (${adjustedX}, ${adjustedY})`, 'serviceLocations');

    // Check for custom icons with cascading fallback system
    const iconConfig = config.customIcons?.[serviceType.code];
    let pinContainer: SVGElement;
    
    // Priority 1: Custom uploaded image
    if (iconConfig?.imageUrl) {
      pinContainer = await createCustomIconPin(service, serviceType, config, adjustedX, adjustedY, iconConfig.imageUrl);
    }
    // Priority 2: Google Material Icon
    else if (iconConfig?.googleIcon) {
      const googleIconPin = createGoogleIconPin(service, serviceType, config, adjustedX, adjustedY, iconConfig.googleIcon);
      pinContainer = googleIconPin || createDefaultServicePin(service, serviceType, config, adjustedX, adjustedY);
    }
    // Priority 3: Default circle pin
    else {
      pinContainer = createDefaultServicePin(service, serviceType, config, adjustedX, adjustedY);
    }

    // Add hover functionality for labels if enabled
    if (config.labelStyling.enabled && config.labelStyling.showLabelOnHover) {
      const pinElements = pinContainer.querySelectorAll('.service-pin');
      pinElements.forEach(pinElement => {
        pinElement.addEventListener('mouseenter', () => {
          showServiceLabel(svgElement, service, config);
        });
        
        pinElement.addEventListener('mouseleave', () => {
          hideServiceLabel(svgElement, service.id.toString());
        });
        
        // Make the pin slightly interactive for hover
        (pinElement as HTMLElement).style.cursor = 'pointer';
      });
    }

    pinLayer.appendChild(pinContainer);
  }

  debugLog(`Created ${serviceLocations.length} service pins with cascading icon system (custom images > Google Material Icons > default circles)`, 'serviceLocations');
}

/**
 * Create service location labels for a specific floor
 */
export function createServiceLabelsForFloor(
  svgElement: SVGSVGElement,
  serviceLocations: ServiceLocationData[],
  config: ServiceLocationConfig
): void {
  if (!svgElement || !config.labelStyling.enabled) return;

  // Clear existing labels
  clearServiceLabels(svgElement);

  // Create or get the service labels layer
  let labelLayer = svgElement.querySelector(`#${SERVICE_LABEL_LAYER_ID}`);
  if (!labelLayer) {
    labelLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    labelLayer.setAttribute('id', SERVICE_LABEL_LAYER_ID);
    svgElement.appendChild(labelLayer);
  }

  serviceLocations.forEach(service => {
    const serviceType = getServiceTypeById(service.serviceType);
    if (!serviceType) return;

    // Create label
    const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
    
    const labelWidth = 80;
    const labelHeight = 30;
    const yOffset = config.labelStyling.yOffset || 0;

    // Apply coordinate transformations for alignment with backend map editor
    let adjustedX = service.x;
    let adjustedY = service.y;
    
    // Apply scaling transformation if enabled
    if (config.coordinateScaling.enabled) {
      const scaleFactor = config.coordinateScaling.scaleFactor;
      const centerX = config.coordinateScaling.centerX;
      const centerY = config.coordinateScaling.centerY;
      
      // Scale the service location coordinates toward/away from the center
      adjustedX = centerX + (service.x - centerX) * scaleFactor;
      adjustedY = centerY + (service.y - centerY) * scaleFactor;
    }
    
    // Apply coordinate offset
    adjustedX += config.coordinateOffset.x;
    adjustedY += config.coordinateOffset.y;

    foreignObject.setAttribute('x', String(adjustedX - labelWidth / 2));
    foreignObject.setAttribute('y', String(adjustedY - labelHeight / 2 + yOffset));
    foreignObject.setAttribute('width', String(labelWidth));
    foreignObject.setAttribute('height', String(labelHeight));
    foreignObject.style.overflow = 'visible';
    foreignObject.style.pointerEvents = 'none';

    const labelDiv = document.createElement('div');
    labelDiv.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
    labelDiv.className = 'service-label';
    labelDiv.innerText = serviceType.name;
    
    // Apply label styling
    labelDiv.style.fontSize = config.labelStyling.fontSize;
    labelDiv.style.fontWeight = config.labelStyling.fontWeight;
    labelDiv.style.color = config.labelStyling.textColor;
    labelDiv.style.textShadow = config.labelStyling.textShadow;
    labelDiv.style.textAlign = 'center';
    labelDiv.style.display = 'flex';
    labelDiv.style.alignItems = 'center';
    labelDiv.style.justifyContent = 'center';
    labelDiv.style.height = '100%';
    labelDiv.style.whiteSpace = 'nowrap';

    // Add hover background if enabled
    if (config.labelStyling.showLabelOnHover) {
      labelDiv.style.backgroundColor = config.labelStyling.hoverBackgroundColor;
      labelDiv.style.borderRadius = config.labelStyling.hoverBorderRadius;
      labelDiv.style.padding = `${config.labelStyling.hoverVerticalPadding} ${config.labelStyling.hoverHorizontalPadding}`;
    }

    foreignObject.appendChild(labelDiv);
    labelLayer.appendChild(foreignObject);
  });

  debugLog(`Created ${serviceLocations.length} service labels`, 'serviceLocations');
}

/**
 * Clear all service pins from the SVG
 */
export function clearServicePins(svgElement: SVGSVGElement): void {
  const pinLayer = svgElement.querySelector(`#${SERVICE_PIN_LAYER_ID}`);
  if (pinLayer) {
    pinLayer.innerHTML = '';
  }
}

/**
 * Clear all service labels from the SVG
 */
export function clearServiceLabels(svgElement: SVGSVGElement): void {
  const labelLayer = svgElement.querySelector(`#${SERVICE_LABEL_LAYER_ID}`);
  if (labelLayer) {
    labelLayer.innerHTML = '';
  }
}

/**
 * Show service label on hover
 */
export function showServiceLabel(
  svgElement: SVGSVGElement,
  service: ServiceLocationData,
  config: ServiceLocationConfig
): void {
  const serviceType = getServiceTypeById(service.serviceType);
  if (!serviceType) return;

  // Remove any existing hover label
  hideServiceLabel(svgElement, service.id.toString());

  // Create or get the service labels layer
  let labelLayer = svgElement.querySelector(`#${SERVICE_LABEL_LAYER_ID}`);
  if (!labelLayer) {
    labelLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    labelLayer.setAttribute('id', SERVICE_LABEL_LAYER_ID);
    svgElement.appendChild(labelLayer);
  }

  // Create hover label (same approach as store pin labels)
  const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
  
  const labelWidth = 200;  // Wider to accommodate longer text
  const labelHeight = 30;  // Smaller height
  const yOffset = config.labelStyling.yOffset || 0;

  // Apply coordinate transformations for alignment with backend map editor
  let adjustedX = service.x;
  let adjustedY = service.y;
  
  // Apply scaling transformation if enabled
  if (config.coordinateScaling.enabled) {
    const scaleFactor = config.coordinateScaling.scaleFactor;
    const centerX = config.coordinateScaling.centerX;
    const centerY = config.coordinateScaling.centerY;
    
    // Scale the service location coordinates toward/away from the center
    adjustedX = centerX + (service.x - centerX) * scaleFactor;
    adjustedY = centerY + (service.y - centerY) * scaleFactor;
  }
  
  // Apply coordinate offset
  adjustedX += config.coordinateOffset.x;
  adjustedY += config.coordinateOffset.y;

  foreignObject.setAttribute('x', String(adjustedX - labelWidth / 2));
  foreignObject.setAttribute('y', String(adjustedY - labelHeight / 2 + yOffset));
  foreignObject.setAttribute('width', String(labelWidth));
  foreignObject.setAttribute('height', String(labelHeight));
  foreignObject.setAttribute('data-service-label-id', service.id.toString());
  foreignObject.style.overflow = 'visible';
  foreignObject.style.pointerEvents = 'none';

  // Create a centering container (same as store labels)
  const containerDiv = document.createElement('div');
  containerDiv.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
  containerDiv.style.width = '100%';
  containerDiv.style.height = '100%';
  containerDiv.style.display = 'flex';
  containerDiv.style.alignItems = 'center';
  containerDiv.style.justifyContent = 'center';

  const labelDiv = document.createElement('div');
  labelDiv.className = 'service-label-hover';
  labelDiv.innerText = serviceType.name;
  
  // Apply styles - make the label size to its content, not the container (same as store labels)
  labelDiv.style.display = 'inline-block';
  labelDiv.style.textAlign = 'center';
  labelDiv.style.fontSize = config.labelStyling.fontSize;
  labelDiv.style.fontWeight = config.labelStyling.fontWeight;
  labelDiv.style.color = config.labelStyling.textColor;
  labelDiv.style.textShadow = config.labelStyling.textShadow;
  labelDiv.style.pointerEvents = 'none';
  labelDiv.style.whiteSpace = 'nowrap';
  labelDiv.style.backgroundColor = config.labelStyling.hoverBackgroundColor;
  labelDiv.style.borderRadius = config.labelStyling.hoverBorderRadius;
  labelDiv.style.padding = `${config.labelStyling.hoverVerticalPadding} ${config.labelStyling.hoverHorizontalPadding}`;
  labelDiv.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';

  containerDiv.appendChild(labelDiv);
  foreignObject.appendChild(containerDiv);
  labelLayer.appendChild(foreignObject);
}

/**
 * Hide service label on mouse leave
 */
export function hideServiceLabel(svgElement: SVGSVGElement, serviceId: string): void {
  const labelLayer = svgElement.querySelector(`#${SERVICE_LABEL_LAYER_ID}`);
  if (labelLayer) {
    const existingLabel = labelLayer.querySelector(`[data-service-label-id="${serviceId}"]`);
    if (existingLabel) {
      existingLabel.remove();
    }
  }
}

 